/**
 * Format a Unix timestamp (ms) to a datetime-local input value in local time.
 * Avoids the UTC conversion issue with toISOString().slice().
 * @param {number} ts - Unix timestamp in milliseconds
 * @returns {string} - Format: "YYYY-MM-DDTHH:mm"
 */
export function timestampToDatetimeLocal(ts) {
  const d = new Date(ts);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Turn a "send forms on" date (YYYY-MM-DD from a date input) into the ISO timestamp
 * the intake-send endpoint queues on: 9:00 AM in the scheduling user's local time.
 * Empty input returns undefined so the caller sends now (key omitted from the request).
 * @param {string} dateStr
 * @returns {string|undefined}
 */
export function formSendTimestamp(dateStr) {
  if (!dateStr) return undefined;
  const d = new Date(`${dateStr}T09:00:00`);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

export function timestampToDateOnly(ts) {
  const d = new Date(ts);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * Local calendar day ("YYYY-MM-DD") of a visit timestamp (ms or seconds) or
 * date string. Date-only strings pass through untouched (no UTC shift).
 */
export function toVisitDateStr(ts) {
  if (ts == null || ts === '') return undefined;
  if (typeof ts === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(ts)) return ts;
  let n = typeof ts === 'number' ? ts : new Date(ts).getTime();
  if (!Number.isFinite(n)) return undefined;
  if (n < 1e12) n *= 1000;
  return timestampToDateOnly(n);
}

export const NOTE_DATE_MIN = '1900-01-01';
export const NOTE_DATE_MAX = '2100-12-31';

/** Template-builder choices for a date field's `dateConstraint` */
export const DATE_CONSTRAINT_OPTIONS = [
  { value: '', label: 'Any date' },
  { value: 'past_incl', label: 'Visit date or earlier' },
  { value: 'past_excl', label: 'Before the visit date' },
  { value: 'future_incl', label: 'Visit date or later' },
  { value: 'future_excl', label: 'After the visit date' },
];

export function dateConstraintToOption(constraint) {
  if (!constraint?.direction) return '';
  return `${constraint.direction}_${constraint.includeToday ? 'incl' : 'excl'}`;
}

export function optionToDateConstraint(option) {
  if (!option) return undefined;
  const [direction, today] = option.split('_');
  return { direction, includeToday: today === 'incl' };
}

const shiftDateOnly = (dateStr, days) => {
  const [y, m, d] = dateStr.split('-').map(Number);
  return timestampToDateOnly(new Date(y, m - 1, d + days).getTime());
};

/**
 * Allowed range for a note date field: the plausible-year window, tightened by
 * the field's dateConstraint relative to the visit date.
 * @param {{direction: 'past'|'future', includeToday: boolean}} [constraint]
 * @param {string} [visitDateStr] - "YYYY-MM-DD"; falls back to today
 * @returns {{min: string, max: string}}
 */
export function noteDateBounds(constraint, visitDateStr) {
  let min = NOTE_DATE_MIN;
  let max = NOTE_DATE_MAX;
  if (constraint?.direction) {
    const ref = visitDateStr || timestampToDateOnly(Date.now());
    if (constraint.direction === 'past') max = constraint.includeToday ? ref : shiftDateOnly(ref, -1);
    if (constraint.direction === 'future') min = constraint.includeToday ? ref : shiftDateOnly(ref, 1);
  }
  return { min, max };
}

/**
 * Validate a note date field value ("YYYY-MM-DD"). Empty values are valid
 * (required-ness is checked separately).
 * @returns {string|null} error message, or null when valid
 */
export function validateNoteDate(value, constraint, visitDateStr) {
  if (value === undefined || value === null || value === '') return null;
  const match = typeof value === 'string' ? value.match(/^(\d{4})-(\d{2})-(\d{2})$/) : null;
  if (!match) return 'Enter a valid date';
  const [year, month, day] = match.slice(1).map(Number);
  const d = new Date(year, month - 1, day);
  if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) return 'Enter a valid date';
  if (value < NOTE_DATE_MIN || value > NOTE_DATE_MAX) return 'Enter a valid date (check the year)';
  if (constraint?.direction) {
    const { min, max } = noteDateBounds(constraint, visitDateStr);
    if (constraint.direction === 'past' && value > max) {
      return constraint.includeToday ? 'Date must be on or before the visit date' : 'Date must be before the visit date';
    }
    if (constraint.direction === 'future' && value < min) {
      return constraint.includeToday ? 'Date must be on or after the visit date' : 'Date must be after the visit date';
    }
  }
  return null;
}

/**
 * Compute age in whole years from a date-only birthday string.
 * Accepts "YYYY-MM-DD" or "MM/DD/YYYY" (2-digit years treated as 19xx).
 * Parses by string-splitting and builds the Date from parts — never
 * `new Date(str)`, which UTC-parses date-only strings and shifts a day
 * in negative-offset timezones.
 * @param {string} birthday
 * @returns {number|null} age in years, or null if unparseable
 */
export function calculateAge(birthday) {
  if (!birthday || typeof birthday !== 'string') return null;
  let year, month, day;
  if (birthday.includes('/')) {
    const parts = birthday.split('/');
    if (parts.length !== 3) return null;
    [month, day, year] = parts.map(Number);
    if (String(parts[2]).length === 2) year += 1900;
  } else {
    const parts = birthday.split('T')[0].split('-');
    if (parts.length !== 3) return null;
    [year, month, day] = parts.map(Number);
  }
  if (!year || !month || !day) return null;
  const birthDate = new Date(year, month - 1, day);
  if (Number.isNaN(birthDate.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
  return age >= 0 && age < 150 ? age : null;
}

/**
 * True when a date-only birthday string ("YYYY-MM-DD" or "MM/DD/YYYY") falls
 * on today's local calendar date. Same string-split parsing as calculateAge —
 * never `new Date(str)`, which shifts date-only strings in negative offsets.
 * @param {string} birthday
 * @returns {boolean}
 */
export function isBirthdayToday(birthday) {
  if (!birthday || typeof birthday !== 'string') return false;
  let month, day;
  if (birthday.includes('/')) {
    const parts = birthday.split('/');
    if (parts.length !== 3) return false;
    [month, day] = parts.map(Number);
  } else {
    const parts = birthday.split('T')[0].split('-');
    if (parts.length !== 3) return false;
    [, month, day] = parts.map(Number);
  }
  if (!month || !day) return false;
  const today = new Date();
  return today.getMonth() + 1 === month && today.getDate() === day;
}
